import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, ShieldCheck, Clock, Award, ArrowRight, Home as HomeIcon,
  Building2, Sofa, CheckCircle2, Star, Phone, Calendar
} from 'lucide-react';
import BeforeAfter from '@/components/dubinko/BeforeAfter';
import { useSiteContent } from '@/hooks/useSiteContent';
import { HOME } from '@/constants/testIds';
import SEO, { buildLocalBusinessSchema } from '@/components/dubinko/SEO';
import TrustStats from '@/components/dubinko/TrustStats';
import FAQ, { buildFaqSchema } from '@/components/dubinko/FAQ';
import { api, resolveImage } from '@/lib/api';
import { trackEvent } from '@/lib/analytics';
import DubiMascot from '@/components/dubinko/DubiMascot';

const SERVICE_META = {
  residential: { icon: HomeIcon, bg: 'bg-emerald-50', fg: 'text-emerald-600', testid: HOME.serviceCardResidential },
  commercial:  { icon: Building2, bg: 'bg-blue-50', fg: 'text-blue-700', testid: HOME.serviceCardCommercial },
  carpet:      { icon: Sofa, bg: 'bg-purple-50', fg: 'text-purple-700', testid: HOME.serviceCardCarpet },
};

const STEPS_FALLBACK = [
  { n: '01', t: 'Izaberite uslugu', d: 'Stan, poslovni prostor ili tepisi – izaberite šta vam treba.' },
  { n: '02', t: 'Zakazivanje', d: 'Odaberite datum i vreme koji vam odgovaraju.' },
  { n: '03', t: 'Dolazimo na adresu', d: 'Profesionalni tim sa profesionalnom opremom.' },
  { n: '04', t: 'Uživajte u sjaju', d: 'Plaćate samo ako ste zadovoljni rezultatom.' },
];

const TESTIMONIALS_FALLBACK = [
  { name: 'Milica J.', city: 'Niš', text: 'Stan je izgledao kao iz kataloga! Profesionalan tim, brzi, ljubazni. Sigurno opet.', rating: 5 },
  { name: 'Marko P.', city: 'Niš', text: 'Oprali su mi kauč i tepihe – ne mogu da verujem koliko su čisti. Preporučujem.', rating: 5 },
  { name: 'Ana D.', city: 'Niška Banja', text: 'Posle renoviranja sam mislila da nikad neću sve to očistiti. Dubinko je rešio za pola dana.', rating: 5 },
];

export default function HomePage() {
  const { content } = useSiteContent();
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    document.title = 'Dubinko — Dubinsko čišćenje u Nišu';
    api.get('/reviews').then(({ data }) => setReviews(data)).catch(() => {});
  }, []);

  const realTestimonials = reviews.slice(0, 3).map((r) => ({
    name: r.customer_name, city: 'Niš', text: r.text, rating: r.rating,
  }));
  const TESTIMONIALS = realTestimonials.length >= 3 ? realTestimonials : TESTIMONIALS_FALLBACK;
  const reviewsAgg = reviews.length ? {
    count: reviews.length,
    avg: reviews.reduce((a, r) => a + r.rating, 0) / reviews.length,
  } : null;

  const schema = buildLocalBusinessSchema(content, reviewsAgg);
  const faqSchema = buildFaqSchema(content.faqs);

  return (
    <main data-testid={HOME.hero}>
      <SEO
        title=""
        description="Dubinko — premium dubinsko čišćenje u Nišu. Stanovi, kuće, kancelarije, tepisi i tapacirani nameštaj. Zakažite termin online."
        path="/"
        schema={[schema, faqSchema]}
      />
      {content.promo_active && content.promo_banner_text && (
        <div className="bg-gradient-to-r from-emerald-600 via-blue-700 to-purple-700 text-white text-center text-sm py-2.5 px-4 font-semibold"
             data-testid="promo-banner">
          <span className="inline-flex items-center gap-2">
            <span className="text-yellow-300">★</span> {content.promo_banner_text}
          </span>
        </div>
      )}
      {/* HERO */}
      <section className="relative aurora-bg grain">
        <div className="container-x pt-12 md:pt-20 pb-20 md:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="tag-pill fade-up">
              <Sparkles className="w-3.5 h-3.5" /> {content.hero_badge}
            </span>
            <h1
              data-testid={HOME.heroTitle}
              className="fade-up delay-1 mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.02] tracking-tight text-slate-900"
            >
              {content.hero_title.split(' ').slice(0, -2).join(' ')}{' '}
              <span style={{
                background: 'linear-gradient(135deg, #059669 0%, #023E8A 50%, #7C3AED 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
                {content.hero_title.split(' ').slice(-2).join(' ')}
              </span>
            </h1>
            <p
              data-testid={HOME.heroSubtitle}
              className="fade-up delay-2 mt-6 text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed"
            >
              {content.hero_subtitle}
            </p>

            <div className="fade-up delay-3 mt-8 flex flex-wrap gap-3">
              <Link
                to="/rezervacija"
                data-testid={HOME.heroBookBtn}
                onClick={() => trackEvent('click_book', { source: 'hero' })}
                className="btn-primary px-6 py-3.5 rounded-full font-semibold inline-flex items-center gap-2"
              >
                <Calendar className="w-4 h-4" /> {content.cta_primary}
              </Link>
              <Link
                to="/kontakt"
                data-testid={HOME.heroQuoteBtn}
                onClick={() => trackEvent('click_quote', { source: 'hero' })}
                className="btn-green px-6 py-3.5 rounded-full font-semibold inline-flex items-center gap-2"
              >
                {content.cta_secondary} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/usluge"
                data-testid={HOME.heroServicesBtn}
                onClick={() => trackEvent('click_services', { source: 'hero' })}
                className="btn-ghost px-6 py-3.5 rounded-full font-semibold"
              >
                Pogledaj usluge
              </Link>
            </div>

            <div className="fade-up delay-4 mt-10 flex flex-wrap gap-x-8 gap-y-4 text-sm text-slate-600">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-600" /> 100% garancija zadovoljstva</div>
              <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-blue-700" /> Tačno na zakazano vreme</div>
              <div className="flex items-center gap-2"><Award className="w-4 h-4 text-purple-700" /> 500+ zadovoljnih klijenata</div>
            </div>
          </div>

          <div className="lg:col-span-5 fade-up delay-2">
            <div className="relative flex flex-col items-center gap-6">
              <DubiMascot
                speech="Zdravo! Ja sam Dubi 👋 Pomažem oko izbora čišćenja."
                onChatOpen={() => window.dispatchEvent(new CustomEvent('dubi:open'))}
              />
              <div className="relative w-full">
                <img
                  src={resolveImage(content.hero_image_url)}
                  alt="Premium deep cleaning"
                  loading="lazy"
                  className="rounded-[2rem] w-full object-cover aspect-[5/3] shadow-2xl"
                />
                <div className="absolute -left-4 -bottom-4 glass rounded-2xl px-4 py-3 max-w-[210px]">
                  <div className="flex items-center gap-2 font-display font-bold text-slate-900 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Sjajan rezultat
                  </div>
                </div>
                <div className="absolute -right-4 top-6 glass rounded-2xl px-3 py-2 text-xs font-semibold flex items-center gap-2">
                  <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                  4.9 / 5
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STATS */}
      <TrustStats title={content.stats_title} stats={content.stats} />

      {/* BEFORE / AFTER */}
      <section className="section-pad bg-slate-50">
        <div className="container-x">
          <div className="max-w-3xl">
            <span className="tag-pill">Pre i posle</span>
            <h2 className="font-display text-4xl sm:text-5xl font-bold mt-4">Vidite razliku. <span className="text-emerald-600">Doslovno.</span></h2>
            <p className="text-slate-600 mt-4 text-lg">Povucite klizač i pogledajte transformaciju koju donosimo svakom prostoru.</p>
          </div>
          <div className="mt-10">
            <BeforeAfter
              testId={HOME.beforeAfter}
              beforeSrc={resolveImage(content.before_image_url)}
              afterSrc={resolveImage(content.after_image_url)}
            />
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="section-pad" data-testid={HOME.servicesGrid}>
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <span className="tag-pill">Naše usluge</span>
              <h2 className="font-display text-4xl sm:text-5xl font-bold mt-4">Šta sve čistimo</h2>
              <p className="text-slate-600 mt-4 text-lg">Tri ključne kategorije usluga — sve sa istom premium pažnjom prema detaljima.</p>
            </div>
            <Link to="/usluge" className="text-blue-700 font-semibold inline-flex items-center gap-2 hover:gap-3 transition-all">
              Sve usluge <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(content.services || []).map((s) => {
              const meta = SERVICE_META[s.key] || { icon: HomeIcon, bg: 'bg-slate-100', fg: 'text-slate-700', testid: 'service-card-other' };
              const Icon = meta.icon;
              return (
                <Link
                  key={s.key}
                  to={`/rezervacija?service=${s.key}`}
                  data-testid={meta.testid}
                  className="service-card group"
                >
                  <div className="ribbon" />
                  <div className={`icon-wrap ${meta.bg} ${meta.fg} group-hover:scale-110 transition-transform`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-display text-xl font-bold mt-5">{s.title}</h3>
                  <p className="text-slate-600 mt-2 text-sm leading-relaxed">{s.description}</p>
                  {s.image_url && (
                    <div className="mt-5 overflow-hidden rounded-2xl">
                      <img src={resolveImage(s.image_url)} alt={s.title} loading="lazy"
                        className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <div className="mt-5 inline-flex items-center gap-2 font-semibold text-slate-900 group-hover:text-blue-700">
                    Zakaži termin <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section-pad bg-slate-950 text-white" data-testid={HOME.howItWorks}>
        <div className="container-x">
          <div className="max-w-3xl">
            <span className="tag-pill" style={{ background: 'rgba(124,58,237,0.18)', color: '#C4B5FD' }}>Kako funkcioniše</span>
            <h2 className="font-display text-4xl sm:text-5xl font-bold mt-4">{content.steps_title || 'Četiri jednostavna koraka'}</h2>
            <p className="text-slate-400 mt-4 text-lg">{content.steps_subtitle || 'Od prvog klika do besprekorno čistog prostora.'}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {(content.steps?.length ? content.steps : STEPS_FALLBACK).map((s) => (
              <div key={s.n} className="relative glass-dark rounded-3xl p-6">
                <div className="font-display text-5xl font-extrabold"
                     style={{
                       background: 'linear-gradient(135deg, #10B981 0%, #7C3AED 100%)',
                       WebkitBackgroundClip: 'text',
                       WebkitTextFillColor: 'transparent',
                     }}>{s.n}</div>
                <div className="mt-4 font-display text-xl font-bold">{s.t}</div>
                <p className="text-slate-400 mt-2 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section-pad" data-testid={HOME.testimonials}>
        <div className="container-x">
          <div className="max-w-3xl">
            <span className="tag-pill">Iskustva klijenata</span>
            <h2 className="font-display text-4xl sm:text-5xl font-bold mt-4">Šta kažu naši klijenti</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="bg-white rounded-3xl border border-slate-200 p-6 hover:shadow-lg transition">
                <div className="flex gap-0.5 text-amber-500">
                  {Array.from({ length: t.rating }).map((_, k) => (
                    <Star key={k} className="w-4 h-4 fill-amber-500" />
                  ))}
                </div>
                <p className="text-slate-700 mt-4 leading-relaxed">&ldquo;{t.text}&rdquo;</p>
                <div className="mt-5 text-sm">
                  <div className="font-display font-bold text-slate-900">{t.name}</div>
                  <div className="text-slate-500">{t.city}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQ title={content.faq_title} subtitle={content.faq_subtitle} faqs={content.faqs} />

      {/* CTA */}
      <section className="section-pad" data-testid={HOME.ctaBottom}>
        <div className="container-x">
          <div className="relative rounded-[2.5rem] overflow-hidden p-10 md:p-16 text-white"
               style={{ background: 'linear-gradient(135deg, #023E8A 0%, #5B21B6 100%)' }}>            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center relative z-10">
              <div>
                <h2 className="font-display text-3xl md:text-5xl font-extrabold leading-tight">
                  {content.cta_bottom_title || 'Spremni za sjajan prostor?'}
                </h2>
                <p className="mt-4 text-blue-100/90 text-lg max-w-lg">
                  {content.cta_bottom_subtitle || 'Zakažite termin u manje od 2 minuta — ili nas pozovite za besplatnu procenu.'}
                </p>
              </div>
              <div className="flex flex-wrap gap-3 md:justify-end">
                <Link to="/rezervacija" className="bg-white text-slate-900 px-6 py-3.5 rounded-full font-semibold inline-flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> Zakaži online
                </Link>
                <a href={`tel:${content.phone}`} className="border border-white/40 px-6 py-3.5 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-white/10">
                  <Phone className="w-4 h-4" /> {content.phone}
                </a>
              </div>
            </div>
            <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full"
                 style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.45), transparent 70%)' }} />
            <div className="absolute -left-20 -bottom-20 w-80 h-80 rounded-full"
                 style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.45), transparent 70%)' }} />
          </div>
        </div>
      </section>
    </main>
  );
}
